import { PolicyFinding } from '@/types/fatigue';

type PolicyDuty = {
  id: string;
  startTime: number;
  endTime: number;
  dutyLengthHours: number;
  startDayIndex: number;
  commuteMinutes: number;
};

const HOURS_PER_DAY = 24;

export const evaluatePolicy = (duties: PolicyDuty[]): PolicyFinding[] => {
  if (duties.length === 0) return [];
  const findings: PolicyFinding[] = [];
  const sorted = [...duties].sort((a, b) => a.startTime - b.startTime);

  for (const duty of sorted) {
    if (duty.dutyLengthHours > 12) {
      findings.push({
        id: `duty-length-${duty.id}`,
        severity: 'breach',
        rule: 'Max 12 hours per duty',
        message: `Duty length exceeds 12 hours (${duty.dutyLengthHours.toFixed(2)}h).`,
        dutyId: duty.id,
      });
    }
    const doorToDoor = duty.dutyLengthHours + duty.commuteMinutes / 60;
    if (doorToDoor > 14) {
      findings.push({
        id: `door-to-door-${duty.id}`,
        severity: 'breach',
        rule: 'Max 14 hours door-to-door',
        message: `Door-to-door time exceeds 14 hours (${doorToDoor.toFixed(2)}h).`,
        dutyId: duty.id,
      });
    }
  }

  for (let i = 1; i < sorted.length; i += 1) {
    const restHours = (sorted[i].startTime - sorted[i - 1].endTime) * HOURS_PER_DAY;
    if (restHours < 12) {
      findings.push({
        id: `rest-gap-${sorted[i].id}`,
        severity: 'breach',
        rule: 'Min 12 hours between duties',
        message: `Rest break is below 12 hours (${restHours.toFixed(2)}h).`,
        dutyId: sorted[i].id,
      });
    }
  }

  const startDays = sorted.map(duty => duty.startDayIndex).sort((a, b) => a - b);
  const dayToDutyId = new Map<number, string>();
  sorted.forEach(duty => {
    dayToDutyId.set(duty.startDayIndex, duty.id);
  });
  for (let i = 0; i < startDays.length; i += 1) {
    const windowStart = startDays[i] - 13;
    const count = startDays.filter(day => day >= windowStart && day <= startDays[i]).length;
    if (count > 13) {
      findings.push({
        id: `consecutive-duties-${startDays[i]}`,
        severity: 'breach',
        rule: 'Max 13 duties in 14 days',
        message: `More than 13 duties within a 14-day window ending day ${startDays[i]}.`,
        dutyId: dayToDutyId.get(startDays[i]),
      });
      break;
    }
  }

  const rollingTotals = computeRollingHours(sorted, 7);
  rollingTotals.forEach(window => {
    if (window.totalHours > 60) {
      findings.push({
        id: `rolling-60-${window.endDutyId}`,
        severity: 'warning',
        rule: 'Max 60 hours in rolling 7 days (rostered)',
        message: `Rolling 7-day total exceeds 60 hours (${window.totalHours.toFixed(2)}h).`,
        dutyId: window.endDutyId,
      });
    }
    if (window.totalHours > 72) {
      findings.push({
        id: `rolling-72-${window.endDutyId}`,
        severity: 'breach',
        rule: 'Max 72 hours in rolling 7 days (actual)',
        message: `Rolling 7-day total exceeds 72 hours (${window.totalHours.toFixed(2)}h).`,
        dutyId: window.endDutyId,
      });
    }
  });

  return findings;
};

const computeRollingHours = (duties: PolicyDuty[], days: number) => {
  const window: { endDutyId: string; totalHours: number }[] = [];
  let startIndex = 0;
  let total = 0;
  const workHours = duties.map(duty => duty.dutyLengthHours + duty.commuteMinutes / 60);

  for (let i = 0; i < duties.length; i += 1) {
    const currentStart = duties[i].startTime;
    while (currentStart - duties[startIndex].startTime > days) {
      total -= workHours[startIndex];
      startIndex += 1;
    }
    total += workHours[i];
    window.push({ endDutyId: duties[i].id, totalHours: total });
  }
  return window;
};
